import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, BookOpen, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PLATFORM_IMAGES } from "@/constants/platformImages";

export default function WordPressIntegration() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/settings', { replace: true })}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-background border border-border/30">
              <img src={PLATFORM_IMAGES.wordpress} alt="WordPress" className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-foreground">WordPress Integration</h1>
              <p className="text-muted-foreground mt-2">
                Connect your WordPress site to publish SEO-optimized articles directly
              </p>
            </div>
          </div>
        </div>

        {/* Prerequisites */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Prerequisites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              To enable integration with WordPress, ensure the following conditions are met.
            </p>
            <p className="font-medium mb-2">Your WordPress site must be either:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Self-hosted through a third-party provider such as Bluehost or WPMU</li>
              <li>or hosted on WordPress.com with a Business Plan or higher</li>
            </ul>
          </CardContent>
        </Card>

        {/* Configuration Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Configuration Checklist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Before proceeding with the integration, verify the following settings on your WordPress site:
            </p>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Secure Connection (SSL)</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Confirm that your website uses HTTPS. If your site's URL begins with <code className="bg-muted px-1 rounded">https://</code>, SSL is active.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Permalink Structure</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Navigate to <code className="bg-muted px-1 rounded">Settings → Permalinks</code> in your WordPress dashboard. Ensure that the permalink structure is not set to "Plain." A more descriptive structure (e.g., "Post name") is recommended.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Application Passwords</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Verify that the "Application Passwords" feature is enabled. This setting allows external applications to connect securely to your WordPress site.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 1: Enter Your Website</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">Access the SEORO platform</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">Navigate to the "Settings" section</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">Select the "Connect" option and enter your website's homepage URL</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-4 rounded-lg mt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900 dark:text-blue-100">Two Connection Options</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                    You can either connect via WordPress OAuth (requires plugin) or add information manually using your WordPress username and application password.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 2 - Manual Entry */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Step 2: Add Information Manually</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              If you choose manual entry, you'll need:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="mt-1">1</Badge>
                <div>
                  <p className="font-medium">WordPress Site URL</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your WordPress site URL (e.g., <code className="bg-muted px-1 rounded">https://yoursite.com</code>)
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">2</Badge>
                <div>
                  <p className="font-medium">WordPress Username</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your WordPress admin username
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Badge className="mt-1">3</Badge>
                <div>
                  <p className="font-medium">WordPress Application Password</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create an Application Password: <code className="bg-muted px-1 rounded">Users → Profile → Application Passwords</code>
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Application passwords are more secure than regular passwords and are required for API access.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 3 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 3: You Are Connected!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Once authorization is complete, you will be redirected back to SEORO with a confirmation message indicating a successful connection.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              If you encounter any issues during this process, please reach out to our support team via the in-app sidebar.
            </p>
          </CardContent>
        </Card>

        {/* Publishing Workflow */}
        <Card>
          <CardHeader>
            <CardTitle>Publishing Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-muted-foreground">
              • Articles marked as "Published" in SEORO will be immediately sent to your connected WordPress site.
            </p>
            <p className="text-muted-foreground">
              • Articles marked as "Scheduled" will be automatically published at the designated time, as long as the WordPress toggle is enabled.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

